import type { Ratings } from '@benzinga/calendar-manager';
import type { DelayedQuote } from '@benzinga/quotes-manager';

export const shorten = (str: string, maxLen: number, append = '...', separator = ' ') => {
  if (str.length <= maxLen) {
    return str;
  }
  return `${str.substr(0, str.lastIndexOf(separator, maxLen))}${append}`;
};

export const formatLarge = (num: number) => {
  if (typeof num !== 'number') {
    return num;
  }

  if (num >= 1000000000000 || num <= -1000000000000) {
    return `${(num / 1000000000000).toFixed(1)?.replace(/\.0$/, '')}T`;
  }

  if (num >= 1000000000 || num <= -1000000000) {
    return `${(num / 1000000000).toFixed(1)?.replace(/\.0$/, '')}B`;
  }

  if (num >= 1000000 || num <= -1000000) {
    return `${(num / 1000000).toFixed(1)?.replace(/\.0$/, '')}M`;
  }

  return `${(num / 1000).toFixed(1)?.replace(/\.0$/, '')}K`;
};

export const formatPercentage = (num: number, round?: boolean) => {
  if (typeof num !== 'number') {
    return num;
  }

  let number = num * 100;
  if (round) {
    number = formatRound(number);
  }

  return `${number}%`;
};

export const formatRound = (num: number) => {
  if (typeof num !== 'number') {
    return num;
  }
  return Math.round(num * 100) / 100;
};

export const formatRoundFixed = (num: number, decimals: number) => {
  if (typeof num !== 'number') {
    return num;
  }
  const rounded = Math.round(num * 100) / 100;
  return rounded.toFixed(decimals);
};

export const getEndOfSession = (schedule: any, type: string) => {
  const sessions = schedule.sessions.filter((session: any) => session.type === type);

  if (sessions?.length) {
    return sessions[0].endTime;
  }

  return null;
};

export const getInterval = (min: number, max: number) => {
  const range = (max - min) / 1000 / 60 / 60;

  if (range < 24) {
    // 1d
    return '2m';
  }
  if (range < 24 * 5) {
    return '10m';
  }
  if (range < 24 * 30) {
    // 1m
    return '1h';
  }
  if (range < 24 * 30 * 3) {
    // 3m
    return '6h';
  }
  if (range < 24 * 365) {
    // 1y
    return '1d';
  }
  if (range < 24 * 365 * 2) {
    // 2y
    return '2d';
  }
  if (range < 24 * 365 * 5) {
    // 5y
    return '5d';
  }
  if (range < 24 * 365 * 10) {
    // 10y
    return '10d';
  }

  return '1mo';
};

export const replaceLinks = (text: string) => {
  const regex = /<a class="ticker" href="(.*?)">(.*?)<\/a>/gm;
  const subst = '<a class="ticker" href="/quote/$2">$2</a>';

  return text?.replace(regex, subst);
};

export const getPlainText = (html: string) => {
  let plainText = '';
  plainText = html?.replace(/<style([\s\S]*?)<\/style>/gi, '');
  plainText = plainText?.replace(/<script([\s\S]*?)<\/script>/gi, '');
  plainText = plainText?.replace(/<\/div>/gi, '\n');
  plainText = plainText?.replace(/<\/li>/gi, '\n');
  plainText = plainText?.replace(/<li>/gi, '  *  ');
  plainText = plainText?.replace(/<\/ul>/gi, '\n');
  plainText = plainText?.replace(/<\/p>/gi, '\n');
  plainText = plainText?.replace(/<br\s*[/]?>/gi, '\n');
  plainText = plainText?.replace(/<[^>]+>/gi, '');
  plainText = plainText?.replace(/&nbsp;/gi, ' ');
  return plainText; //
};

export const getPricePercentageChange = (quote: DelayedQuote, rating: Ratings) => {
  let change = (Number(rating.pt_current) - quote.lastTradePrice) * 100;
  change = change / quote.lastTradePrice;
  return change.toFixed(2);
};
